import axios, { type AxiosRequestConfig, type AxiosResponse } from "axios"

interface ServerOptions {
  appAgent?: string
  axiosRequestConfig?: AxiosRequestConfig
  debug?: boolean
  getAuthorization?: () => Promise<string>
  getOrganizationId?: () => string | null
}

const createServer = (options: ServerOptions) => {
  const server = axios.create(options.axiosRequestConfig)

  server.interceptors.request.use(async (config) => {
    const controller = new AbortController()

    if (options.appAgent) {
      config.headers = Object.assign(
        {
          "App-Agent": options.appAgent,
        },
        config.headers
      )
    }

    if (typeof options.getAuthorization === "function") {
      const authorization = await options.getAuthorization()

      if (!authorization || authorization.includes("Bearer undefined")) {
        controller.abort()
      }

      config.headers = Object.assign(
        {
          Authorization: authorization,
        },
        config.headers
      )
    }

    if (typeof options.getOrganizationId === "function") {
      const organizationId = options.getOrganizationId()

      if (organizationId) {
        config.headers = Object.assign(
          {
            "x-organization-id": organizationId,
          },
          config.headers
        )
      }
    }

    config.signal = controller.signal

    return config
  })

  server.interceptors.response.use(
    (axiosResponse: AxiosResponse) => {
      if (options.debug && axiosResponse.request) {
        console.log(
          `${axiosResponse.request._method}: ${axiosResponse.request._url}`
        )
      }

      return axiosResponse
    },
    (error) => {
      // Handle rate limiting (429 status code)
      if (error.response?.status === 429) {
        const retryAfter = error.response.headers["retry-after"]
        const message = retryAfter
          ? `Too many requests. Please wait ${retryAfter} seconds and try again.`
          : "Too many requests. Please wait a moment and try again."

        // Create a more user-friendly error
        const rateLimitError = new Error(message)
        rateLimitError.name = "RateLimitError"
        return Promise.reject(rateLimitError)
      }

      return Promise.reject(error)
    }
  )

  return server
}

export default createServer
